import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import { BrowserRouter as Router } from "react-router-dom";
import store from "./redux/store";
import { Provider } from "react-redux";
import { WagmiProvider } from "wagmi";
import { currentNetwork } from "./utils/customNetwork";
import { createAppKit } from "@reown/appkit/react";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ReactGA from "react-ga4";
import AOSProvider from "./utils/AOSProvider";
// import { Auth0Provider } from "@auth0/auth0-react";
// 0. Setup queryClient
const queryClient = new QueryClient();

const projectId = process.env.REACT_APP_PROJECT_ID;

// 2. Create wagmiConfig
const metadata = {
  name:
    process.env.REACT_APP_NETWORK === "testnet" ? "MST Testnet" : "MST Mainnet",
  description:
    "MST Blockchain, built by visionary developers, embodies a relentless belief in the transformative power of decentralization.",
  url:
    process.env.REACT_APP_NETWORK === "testnet"
      ? "https://staging.mstblockchain.com/"
      : "https://mstblockchain.com/", // origin must match your domain & subdomain
  icons:
    process.env.REACT_APP_NETWORK === "testnet"
      ? ["https://staging.mstblockchain.com/images/logo/main.png"]
      : [
          "https://imagedelivery.net/_aTEfDRm7z3tKgu9JhfeKA/f6f79fda-3a73-4e9f-2e99-2d9ddc004100/sm",
        ],
};
const wagmiAdapter = new WagmiAdapter({
  networks: [currentNetwork],
  projectId,
});

// useAppKit() throws synchronously on render if createAppKit() hasn't run
// yet, so this must complete before the app mounts — it can't be deferred
// to an idle callback (components like Withdraw/WalletLogin call
// useAppKit() on render, not just on click).
createAppKit({
  adapters: [wagmiAdapter],
  networks: [currentNetwork],
  metadata: metadata,
  projectId,
  features: {
    analytics: true,
    socials: false,
    email: false,
  },
});

const TrackingId = process.env.REACT_APP_TRACKING_ID;
if (TrackingId) {
  ReactGA.initialize(TrackingId);
} else {
  console.warn("Google Analytics (REACT_APP_TRACKING_ID) is not configured.");
}

if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .then((registration) => {
      console.log("Service Worker registered: ", registration);
    })
    .catch((error) => {
      console.log("Service Worker registration failed: ", error);
    });
}

if (
  "serviceWorker" in navigator &&
  process.env.REACT_APP_NETWORK === "testnet"
) {
  window?.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js") // Remove the leading slash
      .then((registration) => {
        console.log("SW registered: ", registration);
      })
      .catch((registrationError) => {
        console.log("SW registration failed: ", registrationError);
      });
  });
}
const isIOS = /iPhone/.test(navigator.userAgent);
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

if (isIOS && isSafari) {
  document.documentElement.classList.add("ios-safari");
}

document.addEventListener(
  "wheel",
  () => {
    const el = document.activeElement;
    if (el && el.tagName === "INPUT" && el.type === "number") el.blur();
  },
  { passive: true },
);

const container = document.getElementById("root");

const appElement = (
  <React.StrictMode>
    <Provider store={store}>
      <Router>
        <WagmiProvider config={wagmiAdapter.wagmiConfig}>
          <QueryClientProvider client={queryClient}>
            <AOSProvider>
              {/* <Auth0Provider
              domain={"https://support.mstvalidator.com/portal/en/home"}
              clientId={process.env.REACT_APP_ZOHO_CLIENTID}
              authorizationParams={{
                redirect_uri: window.location.origin
              }}
            > */}
              <App />
            </AOSProvider>
            {/* </Auth0Provider> */}
          </QueryClientProvider>
        </WagmiProvider>
      </Router>
    </Provider>
  </React.StrictMode>
);

// The static host falls back to a prerendered file (usually web/index.html,
// the homepage) for any URL it has no file for — e.g. /portal/login. That
// fallback's #root is non-empty but holds a DIFFERENT route's markup, so
// "has child nodes" alone is not enough to tell real SSR for THIS route apart
// from a mismatched fallback. ssr-core.cjs stamps data-ssr-route with the
// route each file was actually rendered for; only hydrate when it matches
// where the browser really is, otherwise do a normal client render.
const ssrRoute = container?.getAttribute("data-ssr-route");
const hasSSR =
  container && container.hasChildNodes() && ssrRoute === window.location.pathname;

if (hasSSR) {
  hydrateRoot(container, appElement);
} else {
  if (container) container.innerHTML = "";
  const root = createRoot(container);
  root.render(appElement);
}
